/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { PlaybackEvent } from '../../../event/PlaybackEvent';
import { AdPlaybackEvent } from '../../../event/AdPlaybackEvent';
import { AnalyticsReporter } from '../../reporter/model/AnalyticsReporter';
import { reportGodavariObservation } from '../../reporter/model/GodavariPayload';

export interface GodavariProgressSnapshot {
  position: number;
  playTime: number;
  isBuffering?: boolean;
}

/** Tracks content observations across playback state changes and intervening ads. */
export class GodavariPlayerObserver {
  private contentStarted = false;
  private awaitingProgress = false;
  private adPaused = false;
  private startPosition = 0;
  private startPlayTime = 0;
  private startingBitrate_?: number;
  private lastPlayTime = 0;
  private normalizedWatchTime_ = 0;

  get hasStarted(): boolean {
    return this.contentStarted;
  }

  get startingBitrate(): number | undefined {
    return this.startingBitrate_;
  }

  get normalizedWatchTime(): number {
    return this.normalizedWatchTime_;
  }

  reset(initialPlayTime: number): void {
    this.contentStarted = false;
    this.adPaused = false;
    this.awaitingProgress = false;
    this.startPosition = 0;
    this.startPlayTime = 0;
    this.startingBitrate_ = undefined;
    this.lastPlayTime = Number.isFinite(initialPlayTime) && initialPlayTime >= 0 ? initialPlayTime : 0;
    this.normalizedWatchTime_ = 0;
  }

  onPlaybackStarted(
    reporter: AnalyticsReporter,
    snapshot: GodavariProgressSnapshot,
    isContentPlayback: boolean,
    buildMetadata: () => { [key: string]: any },
  ): void {
    if (!this.contentStarted) {
      reportGodavariObservation(reporter, PlaybackEvent.PLAYBACK_START);
    }
    if (isContentPlayback) {
      this.adPaused = false;
    }
    if (isContentPlayback && this.contentStarted) {
      reporter.reportEvent(PlaybackEvent.PLAYBACK_RESUMED, buildMetadata());
    } else {
      if (!this.contentStarted && !this.awaitingProgress) {
        this.awaitingProgress = true;
        this.startPosition = snapshot.position;
        this.startPlayTime = snapshot.playTime;
      }
      // Ad playback reports STARTED repeatedly; only a resume that follows an ad pause is an AdResume.
      if (!isContentPlayback && this.adPaused) {
        this.adPaused = false;
        reporter.reportEvent(AdPlaybackEvent.AD_RESUMED_EVENT);
      }
    }
  }

  /** Call only for progress in the main player context outside an ad break. */
  onProgress(reporter: AnalyticsReporter, snapshot: GodavariProgressSnapshot): void {
    if (
      this.awaitingProgress &&
      !snapshot.isBuffering &&
      (snapshot.position > this.startPosition || snapshot.playTime > this.startPlayTime)
    ) {
      this.awaitingProgress = false;
      this.contentStarted = true;
      reportGodavariObservation(reporter, PlaybackEvent.PLAYBACK_STARTED);
    }
  }

  onPaused(isContentPlayback = true): void {
    this.awaitingProgress = false;
    this.adPaused = !isContentPlayback;
  }

  captureStartingBitrate(bitrate: number | undefined): void {
    if (this.startingBitrate_ === undefined && typeof bitrate === 'number' && Number.isFinite(bitrate) && bitrate > 0) {
      this.startingBitrate_ = Math.trunc(bitrate / 1000);
    }
  }

  updateWatchTime(playTime: number, isContentPlayback: boolean): void {
    if (!Number.isFinite(playTime) || playTime < 0) {
      return;
    }
    if (isContentPlayback) {
      this.normalizedWatchTime_ += Math.max(0, playTime - this.lastPlayTime);
    }
    // Advance the baseline during ads too, so the next content update excludes ad time.
    this.lastPlayTime = playTime;
  }
}
